import { useState, useEffect } from "react"
import CommonPagination from "../../../common/components/pagination/CommonPagination"
import TabSelector from "../../../common/components/tab-selector/TabSelector"
import styles from "./RequestPage.module.scss"
import { AssessmentStatus, type Leads } from "../../../model/leads/leads"
import RequestTab, { RequestType } from "./components/RequestTabs"
import RequestDetail from "./components/RequestDetail"
import { useLazyGetRequestQuery  } from "../../../service/leads-service"

const RequestPage = () => {
  const [selectedTab, setSelectedTab] = useState(1)
  const [selectedTabType, setSelectedTabType] = useState(RequestType.New)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [currentItem, setCurrentItem] = useState<Leads>()

  const getStatusesForTab = (tabType: RequestType): AssessmentStatus[] => {
    switch (tabType) {
      case RequestType.New:
        return [AssessmentStatus.CheckingDigitalLoadAssessment]
      case RequestType.Active:
        return [
          AssessmentStatus.CarringOutPhysicalLoadAssessment,
          AssessmentStatus.Pending,
          AssessmentStatus.SendingProductsThatFit,
          AssessmentStatus.ScheduledPhysicalLoadAssessment,
          AssessmentStatus.RescheduledPhysicalLoadAssessment,
          AssessmentStatus.OverduePhysicalLoadAssessment
        ]
      case RequestType.Closed:
        return [AssessmentStatus.Final]
      default:
        return [AssessmentStatus.ChosingProvider]
    }
  }

const [getNewRequests, {
    data: newRequestsResponse,
    isLoading: isLoadingNew,
    isFetching: isFetchingNew,
  }] = useLazyGetRequestQuery()

  const [getActiveRequests, {
    data: activeRequestsResponse,
    isLoading: isLoadingActive,
    isFetching: isFetchingActive,
  }] = useLazyGetRequestQuery()

  const [getClosedRequests, {
    data: closedRequestsResponse,
    isLoading: isLoadingClosed,
    isFetching: isFetchingClosed,
  }] = useLazyGetRequestQuery();

  useEffect(() => {
    const fetchData = () => {
      switch (selectedTab) {
        case 1:
          getNewRequests({
            page: currentPage,
            pageSize: pageSize,
            statuses: getStatusesForTab(RequestType.New),
          })
          break
        case 2:
          getActiveRequests({
            page: currentPage,
            pageSize: pageSize,
            statuses: getStatusesForTab(RequestType.Active),
          })
          break
        case 3:
          getClosedRequests({
            page: currentPage,
            pageSize: pageSize,
            statuses: getStatusesForTab(RequestType.Closed),
          })
          break
      }
    }

    fetchData()
  }, [selectedTab, currentPage, pageSize, getNewRequests, getActiveRequests, getClosedRequests])

  useEffect(() => {
    // Load data for all tabs on component mount
    getNewRequests({
      page: 1,
      pageSize: pageSize,
      statuses: getStatusesForTab(RequestType.New),
    })
    
    getActiveRequests({
      page: 1,
      pageSize: pageSize,
      statuses: getStatusesForTab(RequestType.Active),
    })
    
    getClosedRequests({
      page: 1,
      pageSize: pageSize,
      statuses: getStatusesForTab(RequestType.Closed),
    })
  }, [])

  const getCurrentData = () => {
    switch (selectedTab) {
      case 1:
        return {
          response: newRequestsResponse,
          isLoading: isLoadingNew,
          isFetching: isFetchingNew,
        }
      case 2:
        return {
          response: activeRequestsResponse,
          isLoading: isLoadingActive,
          isFetching: isFetchingActive,
        }
      case 3:
        return {
          response: closedRequestsResponse,
          isLoading: isLoadingClosed,
          isFetching: isFetchingClosed,
        }
      default:
        return {
          response: newRequestsResponse,
          isLoading: isLoadingNew,
          isFetching: isFetchingNew,
        }
    }
  }

  const currentData = getCurrentData()
  const totalPages = currentData.response?.totalPages ?? 1
  const items = currentData.response?.items ?? []

  const changeTab = (tabId: number, type: RequestType) => {
    setSelectedTab(tabId)
    setSelectedTabType(type)
    setCurrentPage(1)
  }

  const handleSelectedItemClicked = (item: Leads) => {
    setCurrentItem(item)
  }

  const getTab = () => [
    {
      id: 1,
      title: "New Requests",
      onClick: () => changeTab(1, RequestType.New),
      count: newRequestsResponse?.count ?? 0,
    },
    {
      id: 2,
      title: "Active Requests",
      onClick: () => changeTab(2, RequestType.Active),
      count: activeRequestsResponse?.count ?? 0,
    },
    {
      id: 3,
      title: "Closed Requests",
      onClick: () => changeTab(3, RequestType.Closed),
      count: closedRequestsResponse?.count ?? 0,
    },
  ]

  const renderTabContent = () => {
    const { isLoading, isFetching } = currentData

    switch (selectedTab) {
      case 1:
        return (
          <RequestTab
            isLoading={isLoading || isFetching}
            onItemClicked={handleSelectedItemClicked}
            type={RequestType.New}
            data={items}
          />
        )
      case 2:
        return (
          <RequestTab
            isLoading={isLoading || isFetching}
            onItemClicked={handleSelectedItemClicked}
            type={RequestType.Active}
            data={items}
          />
        )
      default:
        return (
          <RequestTab
            isLoading={isLoading || isFetching}
            onItemClicked={handleSelectedItemClicked}
            type={RequestType.Closed}
            data={items}
          />
        )
    }
  }

  return (
    <div>
      {currentItem ? (
        <RequestDetail data={currentItem} type={selectedTabType} onBack={()=>setCurrentItem(undefined)} />
      ) : (
        <div className={styles.tabSelectorWrapper}>
          <div className={styles.tabContainer}>
            <TabSelector tabs={getTab()} selectedTab={selectedTab} theme="yellow" />
          </div>
          {renderTabContent()}
          <div className={styles.paginationContainer}>
            <CommonPagination
              currentPage={currentPage}
              isLoading={isFetchingNew || isFetchingActive || isFetchingClosed}
              numberOfPages={totalPages}
              onPageChange={(page: number) => {
                setCurrentPage(page)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default RequestPage

